import React, { useState } from 'react'
import { Container, Row, Col, Button, Form, Modal, Badge } from 'react-bootstrap'
import axios from 'axios'
import Swal from 'sweetalert2'

import Card from '../../../components/Card'
import ProfileHeader from '../../../components/profile-header'
import img6 from '../../../assets/images/page-img/profile-bg6.jpg'
import NoDataFound from '../../../components/noDataFound'

const SAVED_CARDS_KEY = 'harmonydaycare_saved_cards'

const getSavedCards = () => {
  try {
    const raw = localStorage.getItem(SAVED_CARDS_KEY)
    return raw ? JSON.parse(raw) : []
  } catch (error) {
    return []
  }
}

const saveCards = (cards) => {
  localStorage.setItem(SAVED_CARDS_KEY, JSON.stringify(cards))
}

const formatBrand = (brand) => {
  if (!brand) return 'Card'
  return brand.charAt(0).toUpperCase() + brand.slice(1)
}

const formatCreatedDate = (createdAt) => {
  if (!createdAt) return '-'
  const date = new Date(createdAt)
  return date.toLocaleDateString()
}

const getCardTheme = (brand = '') => {
  const key = String(brand).toLowerCase()

  if (key === 'visa') {
    return 'linear-gradient(135deg, #11235e 0%, #0052cc 50%, #0f2b8f 100%)'
  }

  if (key === 'mastercard') {
    return 'linear-gradient(135deg, #ff5f00 0%, #eb001b 52%, #d61f44 100%)'
  }

  if (key === 'amex' || key === 'american express') {
    return 'linear-gradient(135deg, #0057a0 0%, #0077d4 48%, #00a2ff 100%)'
  }

  if (key === 'discover') {
    return 'linear-gradient(135deg, #ff6a00 0%, #ff8d00 52%, #ffb100 100%)'
  }

  return 'linear-gradient(135deg, #283048 0%, #4b6cb7 100%)'
}

const BrandLogo = ({ brand }) => {
  const key = String(brand).toLowerCase()
  const boxStyle = {
    minWidth: '72px',
    height: '34px',
    borderRadius: '8px',
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    border: '1px solid rgba(255, 255, 255, 0.25)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '0 8px'
  }

  if (key === 'visa') {
    return (
      <div style={boxStyle}>
        <svg width="56" height="16" viewBox="0 0 112 32" role="img" aria-label="Visa logo">
          <text x="56" y="23" textAnchor="middle" fill="#ffffff" fontWeight="900" fontStyle="italic" fontSize="24" fontFamily="Arial, sans-serif">VISA</text>
        </svg>
      </div>
    )
  }

  if (key === 'mastercard') {
    return (
      <div style={boxStyle}>
        <svg width="56" height="20" viewBox="0 0 112 40" role="img" aria-label="Mastercard logo">
          <circle cx="47" cy="20" r="14" fill="#eb001b" />
          <circle cx="65" cy="20" r="14" fill="#f79e1b" fillOpacity="0.95" />
          <circle cx="56" cy="20" r="10" fill="#ff5f00" fillOpacity="0.85" />
        </svg>
      </div>
    )
  }

  if (key === 'amex' || key === 'american express') {
    return (
      <div style={boxStyle}>
        <svg width="58" height="18" viewBox="0 0 116 36" role="img" aria-label="Amex logo">
          <rect x="1" y="1" width="114" height="34" rx="4" fill="#0b6fc7" stroke="#7cc6ff" strokeWidth="2" />
          <text x="58" y="23" textAnchor="middle" fill="#ffffff" fontWeight="800" fontSize="14" fontFamily="Arial, sans-serif">AMEX</text>
        </svg>
      </div>
    )
  }

  if (key === 'discover') {
    return (
      <div style={boxStyle}>
        <svg width="60" height="18" viewBox="0 0 120 36" role="img" aria-label="Discover logo">
          <text x="60" y="23" textAnchor="middle" fill="#ffffff" fontWeight="800" fontSize="14" fontFamily="Arial, sans-serif">DISCOVER</text>
          <ellipse cx="82" cy="20" rx="20" ry="8" fill="#ff8c00" fillOpacity="0.45" />
        </svg>
      </div>
    )
  }

  return (
    <div style={boxStyle}>
      <span style={{ fontSize: '11px', letterSpacing: '1px', color: 'white', opacity: 0.9 }}>
        {formatBrand(brand)}
      </span>
    </div>
  )
}

function AddCardForm({ onAdded }) {
  const [holderName, setHolderName] = useState('')
  const [cardNumber, setCardNumber] = useState('')
  const [expMonth, setExpMonth] = useState('01')
  const [expYear, setExpYear] = useState(String(new Date().getFullYear()))
  const [cvv, setCvv] = useState('')
  const [zip, setZip] = useState('')
  const [city, setCity] = useState('')
  const [region, setRegion] = useState('')
  const [streetAddress, setStreetAddress] = useState('')
  const [country, setCountry] = useState('US')
  const [submitting, setSubmitting] = useState(false)

  const currentYear = new Date().getFullYear()
  const yearsArray = Array.from({ length: 10 }, (_, index) => String(currentYear + index))

  const formatCardInput = (value) => {
    const raw = value.replace(/\D/g, '').slice(0, 16)
    return raw.replace(/(.{4})/g, '$1 ').trim()
  }

  const detectCardBrand = (numberValue) => {
    const digits = numberValue.replace(/\D/g, '')
    if (/^4/.test(digits)) return 'Visa'
    if (/^5[1-5]/.test(digits)) return 'Mastercard'
    if (/^3[47]/.test(digits)) return 'Amex'
    if (/^6(?:011|5)/.test(digits)) return 'Discover'
    return 'Card'
  }

  const addNewCard = async () => {
    if (!holderName.trim()) {
      Swal.fire({ title: 'Required', text: 'Please enter card holder name.', icon: 'warning', confirmButtonText: 'Okay' })
      return
    }

    const cleanNumber = cardNumber.replace(/\s/g, '')
    if (cleanNumber.length < 12) {
      Swal.fire({ title: 'Required', text: 'Please enter a valid card number.', icon: 'warning', confirmButtonText: 'Okay' })
      return
    }

    if (!cvv.trim() || cvv.trim().length < 3) {
      Swal.fire({ title: 'Required', text: 'Please enter a valid CVV.', icon: 'warning', confirmButtonText: 'Okay' })
      return
    }

    setSubmitting(true)
    try {
      const tokenApiUrl = process.env.REACT_APP_QUICKBOOK_TOKEN_URL || 'https://api.intuit.com/quickbooks/v4/payments/tokens'

      const cardPayload = {
        name: holderName.trim(),
        number: cleanNumber,
        expMonth,
        expYear,
        cvc: cvv.trim(),
        address: {
          postalCode: zip,
          country: country || 'US',
          region,
          streetAddress,
          city
        }
      }

      const tokenResponse = await axios.post(
        tokenApiUrl,
        { card: cardPayload },
        { headers: { 'Content-Type': 'application/json' } }
      )

      const quickbookToken = tokenResponse?.data?.value
      console.log('Received QuickBooks token:', quickbookToken)
      if (!quickbookToken) {
        Swal.fire({ title: 'Error', text: 'Unable to tokenize this card with QuickBooks.', icon: 'error', confirmButtonText: 'Okay' })
        return
      }

      const savedCards = getSavedCards()
      const cardData = {
        id: quickbookToken,
        holderName: holderName.trim(),
        brand: detectCardBrand(cleanNumber),
        last4: cleanNumber.slice(-4),
        expMonth,
        expYear,
        createdAt: Date.now(),
      }

      const updated = [cardData, ...savedCards]
      saveCards(updated)
      onAdded(updated)
      setHolderName('')
      setCardNumber('')
      setExpMonth('01')
      setExpYear(String(currentYear))
      setCvv('')
      setZip('')
      setCity('')
      setRegion('')
      setStreetAddress('')
      setCountry('US')

      Swal.fire({ title: 'Saved', text: 'QuickBooks card token saved successfully.', icon: 'success', confirmButtonText: 'Okay' })
    } catch (error) {
      const message = error?.response?.data?.errors?.[0]?.message || 'Something went wrong while saving card.'
      Swal.fire({ title: 'Error', text: message, icon: 'error', confirmButtonText: 'Okay' })
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <>
      <Form.Group className="mb-3">
        <Form.Label className="fw-semibold">Card Holder Name</Form.Label>
        <Form.Control
          type="text"
          value={holderName}
          onChange={(e) => setHolderName(e.target.value)}
          placeholder="Enter card holder name"
        />
      </Form.Group>

      <Form.Group className="mb-3">
        <Form.Label className="fw-semibold">Card Details</Form.Label>
        <Form.Control
          type="text"
          value={cardNumber}
          onChange={(e) => setCardNumber(formatCardInput(e.target.value))}
          placeholder="1234 5678 9012 3456"
        />
      </Form.Group>

      <Row>
        <Col xs={4}>
          <Form.Group className="mb-3">
            <Form.Label className="fw-semibold">Month</Form.Label>
            <Form.Select value={expMonth} onChange={(e) => setExpMonth(e.target.value)}>
              {Array.from({ length: 12 }, (_, index) => String(index + 1).padStart(2, '0')).map((month) => (
                <option key={month} value={month}>{month}</option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>
        <Col xs={4}>
          <Form.Group className="mb-3">
            <Form.Label className="fw-semibold">Year</Form.Label>
            <Form.Select value={expYear} onChange={(e) => setExpYear(e.target.value)}>
              {yearsArray.map((year) => (
                <option key={year} value={year}>{year}</option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>
        <Col xs={4}>
          <Form.Group className="mb-3">
            <Form.Label className="fw-semibold">CVV</Form.Label>
            <Form.Control
              type="password"
              value={cvv}
              onChange={(e) => setCvv(e.target.value.replace(/\D/g, '').slice(0, 4))}
              placeholder="123"
            />
          </Form.Group>
        </Col>
      </Row>

      <Form.Group className="mb-3">
        <Form.Label className="fw-semibold">Street Address</Form.Label>
        <Form.Control
          type="text"
          value={streetAddress}
          onChange={(e) => setStreetAddress(e.target.value)}
          placeholder="Street address"
        />
      </Form.Group>

      <Row>
        <Col md={4}>
          <Form.Group className="mb-3">
            <Form.Label className="fw-semibold">City</Form.Label>
            <Form.Control
              type="text"
              value={city}
              onChange={(e) => setCity(e.target.value)}
              placeholder="City"
            />
          </Form.Group>
        </Col>
        <Col md={4}>
          <Form.Group className="mb-3">
            <Form.Label className="fw-semibold">State/Region</Form.Label>
            <Form.Control
              type="text"
              value={region}
              onChange={(e) => setRegion(e.target.value)}
              placeholder="State"
            />
          </Form.Group>
        </Col>
        <Col md={4}>
          <Form.Group className="mb-3">
            <Form.Label className="fw-semibold">ZIP</Form.Label>
            <Form.Control
              type="text"
              value={zip}
              onChange={(e) => setZip(e.target.value)}
              placeholder="ZIP"
            />
          </Form.Group>
        </Col>
      </Row>

      <Form.Group className="mb-3">
        <Form.Label className="fw-semibold">Country</Form.Label>
        <Form.Control
          type="text"
          value={country}
          onChange={(e) => setCountry(e.target.value.toUpperCase())}
          placeholder="US"
          maxLength={2}
        />
      </Form.Group>

      <Button className="w-100" onClick={addNewCard} disabled={submitting}>
        {submitting ? 'Saving...' : 'Add New Card (QuickBooks)'}
      </Button>
    </>
  )
}

const SavedCardList = () => {
  const [cards, setCards] = useState(getSavedCards())
  const [showQuickbookModal, setShowQuickbookModal] = useState(false)
  const [quickbookCheckoutUrl, setQuickbookCheckoutUrl] = useState('')
  const [quickbookMode, setQuickbookMode] = useState('live')
  const [amount, setAmount] = useState('')
  const [selectedCardId, setSelectedCardId] = useState('')

  const removeCard = (id) => {
    const updated = cards.filter((card) => card.id !== id)
    setCards(updated)
    saveCards(updated)
  }

  const buildQuickbookPaymentUrl = (isTest = false) => {
    const quickbookUrl = process.env.REACT_APP_QUICKBOOK_PAYMENT_URL
    const parsedAmount = Number(amount)

    if (!selectedCardId) {
      Swal.fire({ title: 'Required', text: 'Please select a saved card.', icon: 'warning', confirmButtonText: 'Okay' })
      return null
    }

    if (!parsedAmount || parsedAmount <= 0) {
      Swal.fire({ title: 'Required', text: 'Please enter a valid amount.', icon: 'warning', confirmButtonText: 'Okay' })
      return null
    }

    if (!quickbookUrl) {
      Swal.fire({
        title: 'Quickbook URL Missing',
        text: 'Set REACT_APP_QUICKBOOK_PAYMENT_URL in env to enable Quickbook checkout.',
        icon: 'info',
        confirmButtonText: 'Okay'
      })
      return null
    }

    const selectedCard = cards.find((card) => card.id === selectedCardId)
    return `${quickbookUrl}?amount=${encodeURIComponent(parsedAmount.toFixed(2))}&card_last4=${encodeURIComponent(selectedCard?.last4 || '')}&card_brand=${encodeURIComponent(selectedCard?.brand || '')}&test_mode=${isTest ? '1' : '0'}`
  }

  const openQuickbookModal = (isTest = false) => {
    setQuickbookMode(isTest ? 'test' : 'live')
    setQuickbookCheckoutUrl('')
    setAmount('')
    setSelectedCardId(cards[0]?.id || '')
    setShowQuickbookModal(true)
  }

  const handlePayAmount = () => {
    const checkoutUrl = buildQuickbookPaymentUrl(quickbookMode === 'test')
    if (!checkoutUrl) return
    setQuickbookCheckoutUrl(checkoutUrl)
  }

  return (
    <>
      <ProfileHeader title="Saved Cards" img={img6} />
      <div id="content-page" className="content-page">
        <Container>
          <Row className="g-4">
            <Col lg={8}>
              <Card>
                <Card.Header className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                  <div className="header-title">
                    <h4 className="card-title mb-1">Saved Card List</h4>
                    <p className="text-muted mb-0">Manage your cards and use one-tap payment flow.</p>
                  </div>
                  <Badge bg="primary" pill>{cards.length} Cards</Badge>
                </Card.Header>
                <Card.Body>
                  {cards.length === 0 ? (
                    <NoDataFound txtData="No saved cards found." />
                  ) : (
                    <div className="list-group list-group-flush">
                      {cards.map((card, index) => (
                        <div
                          key={card.id}
                          className="list-group-item px-0 py-3"
                          style={{ borderTop: index === 0 ? 'none' : undefined }}
                        >
                          <div className="d-flex align-items-center gap-3">
                            {/* Brand icon */}
                            <div
                              style={{
                                width: '48px',
                                height: '32px',
                                borderRadius: '6px',
                                background: getCardTheme(card.brand),
                                flexShrink: 0,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}
                            >
                              <span style={{ fontSize: '9px', fontWeight: '700', color: '#fff', letterSpacing: '0.5px', textTransform: 'uppercase' }}>
                                {formatBrand(card.brand)}
                              </span>
                            </div>

                            {/* Card info */}
                            <div className="flex-grow-1 min-width-0">
                              <div className="d-flex align-items-center gap-2 mb-1">
                                <span className="fw-semibold text-dark" style={{ fontFamily: 'monospace', fontSize: '14px', letterSpacing: '2px' }}>
                                  •••• •••• •••• {card.last4}
                                </span>
                                <Badge bg="light" text="dark" className="border" style={{ fontSize: '11px', fontWeight: '500' }}>
                                  Exp {String(card.expMonth).padStart(2, '0')}/{String(card.expYear).slice(-2)}
                                </Badge>
                              </div>
                              <div className="text-muted" style={{ fontSize: '12px' }}>
                                {card.holderName} &nbsp;·&nbsp; Added {formatCreatedDate(card.createdAt)}
                              </div>
                            </div>

                            {/* Remove */}
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() => removeCard(card.id)}
                              style={{ flexShrink: 0, fontSize: '12px' }}
                            >
                              Remove
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </Card.Body>
              </Card>
            </Col>

            <Col lg={4}>
              <Card className="mb-4">
                <Card.Header>
                  <div className="header-title">
                    <h5 className="card-title mb-0">Add New Card</h5>
                  </div>
                </Card.Header>
                <Card.Body>
                  <AddCardForm onAdded={setCards} />
                </Card.Body>
              </Card>

              <Card>
                <Card.Header>
                  <div className="header-title">
                    <h5 className="card-title mb-0">Quickbook Payment</h5>
                  </div>
                </Card.Header>
                <Card.Body>
                  <p className="text-muted small mb-3">Open secure checkout in a modal and pay using your selected saved card.</p>
                  <Button className="w-100" variant="outline-primary" onClick={() => openQuickbookModal(true)} disabled={cards.length === 0}>Test</Button>
                  {cards.length === 0 && (
                    <small className="text-danger d-block mt-2">Add at least one card to continue.</small>
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>

          <Modal show={showQuickbookModal} onHide={() => setShowQuickbookModal(false)} centered size="sm" scrollable>
            <Modal.Header closeButton>
              <Modal.Title>{quickbookMode === 'test' ? 'Quickbook Test Payment' : 'Quickbook Payment'}</Modal.Title>
            </Modal.Header>
            <Modal.Body style={{ maxHeight: '60vh', overflowY: 'auto', paddingBottom: '12px' }}>
              {quickbookCheckoutUrl ? (
                <iframe
                  src={quickbookCheckoutUrl}
                  title="Quickbook Payment"
                  style={{ width: '100%', height: '46vh', minHeight: '280px', border: 0 }}
                />
              ) : (
                <>
                  <Badge bg={quickbookMode === 'test' ? 'warning' : 'success'} text={quickbookMode === 'test' ? 'dark' : 'light'} className="mb-3">
                    {quickbookMode === 'test' ? 'Test Mode' : 'Live Mode'}
                  </Badge>

                  <Form.Group className="mb-3">
                    <Form.Label>Select Saved Card</Form.Label>
                    <Form.Select value={selectedCardId} onChange={(e) => setSelectedCardId(e.target.value)}>
                      <option value="">Select card</option>
                      {cards.map((card) => (
                        <option key={card.id} value={card.id}>
                          {card.brand} ending {card.last4} ({card.expMonth}/{card.expYear})
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Amount</Form.Label>
                    <Form.Control
                      type="number"
                      value={amount}
                      min="0"
                      step="0.01"
                      onChange={(e) => setAmount(e.target.value)}
                      placeholder="Enter amount"
                    />
                  </Form.Group>

                  <Button className="w-100" onClick={handlePayAmount}>Pay Amount</Button>
                </>
              )}
            </Modal.Body>
          </Modal>
        </Container>
      </div>
    </>
  )
}

export default SavedCardList
